import React from 'react';
import InfoTooltip from '../../compat/InfoTooltip';
import PropTypes from 'prop-types';

import tick from '../../../../image/Icons/tick.svg';
import arrowForward from '../../../../image/Icons/arrow-forward.svg';
import doubleTick from '../../../../image/Icons/double-tick.svg';

import './PackageCard.css'

/**
 * PackageCard.
 * Component for handling product package selection.
 *
 * @author  Manny
 * @param   {String}      props.method            Optional industry params.
 * @param   {Function}    props.onSelectPackage   Handles onClick for selecting a package.
 * @param   {Number}      props.packageIndex      Number of package index.
 * @param   {String}      props.selectedIndustry  Name of selected industry.
 * @param   {Object}      props.singlePackage     Name of selected package.
 * @param   {Boolean}     props.isMobile          Determines if tables are for mobile format.
 * @returns {JSX.Element} The component markup.
 */

const PackageCard = ({
  method,
  onSelectPackage,
  packageIndex,
  selectedIndustry,
  singlePackage,
  isMobile
}) => {
  const isLite = singlePackage.key === 'lite';
  const isPremium = singlePackage.key === 'premium';
  let classNames = require('classnames');
  let colSize = classNames({
    'col-sm-3 no-padding card-column': !isMobile,
    'col-sm-4 no-right-padding package-col-mobile mobile-card-column': isMobile
  });
  const doubleTickText = {
    base:    "Everything in Lite plus",
    premium: "Everything in Base plus",
    pro:     "Everything in Premium plus"
  } [singlePackage.key] || "";

  return (
    <div id={isPremium ? "best-value-column" : ""} className={colSize} key={packageIndex}>
      <div id={isPremium ? "best-value-card" : ""} className={`panel-container package-card ${isPremium ? "best-value-card" : ""}`}>
        {isPremium && <div className='best-value-tag captions'><b>BEST VALUE</b></div>}
        <p
          id={`package_title_${singlePackage.key}`}
          className='no-margin package-card-title'
        >
          {singlePackage.title}
        </p>
        <p className="package-blurb">
          {singlePackage.blurb}
        </p>
        <p className='package-selection-h4 no-margin'>
          <span><b style={{fontWeight: 700, fontSize: "30px", lineHeight: "30px"}}>${singlePackage.base_price.toFixed(2)}</b></span>
          <span className='captions'>/MTH</span>
        </p>
        <p className='sub-text padding-top-5 footnote-text' style={{color: "#1e1405", fontSize: "10px", opacity: 0.6, margin: 0}}>{singlePackage.sub_text_1}</p>
        <p className='sub-text margin-bottom-10 footnote-text'style={{color: "#1e1405", fontSize: "10px", opacity: 0.6, margin: 0}}>{singlePackage.sub_text_2}</p>
        <button
          id={`select_package_btn_${singlePackage.key}`}
          type='button'
          className={`btn btn-arrow-move full-width no-margin ${isPremium ? "best-value-btn" : "btn-select-package"}`}
          onClick={() => {
            onSelectPackage(selectedIndustry, singlePackage.key);
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            width: '100%',
            padding: '12px 24px',
          }}
        >
          Select Package
          <img src={arrowForward} alt='>' className='arrow-forward'/>
        </button>
        <hr/>
        {singlePackage?.features?.filteredFeatures && (
          <div className='features-list'>
            { !isLite &&
              <div className='features-container body'>
                <div>
                  <img src={doubleTick} alt='yes' className='margin-right-5 tick-margin' />
                </div>
                <div className='column title-column'>
                  <b>{doubleTickText}</b>
                </div>
              </div>
            }
            {singlePackage.features.filteredFeatures.map((feature, featureKey) => {
              if (feature) {
                return (
                  <div className='features-container body' key={featureKey}>
                    <div>
                      <img src={tick} alt='yes' className='margin-right-5' />
                    </div>
                    <div className='column title-column'>{feature.title}</div>
                    <div className="column tooltip-column text-right">
                      {(feature.description || feature.descriptionHeader) && (
                        <InfoTooltip text={feature.description || ''} title={feature.descriptionHeader || ''}>
                          <span style={{
                            alignItems: "center",
                            border: "1px solid #79848b",
                            borderRadius: "50%",
                            cursor: "pointer",
                            display: "flex",
                            fontFamily: "TWK Everett, sans-serif",
                            fontSize: "13px",
                            height: "15px",
                            justifyContent: "center",
                            marginTop: "1px",
                            width: "15px"
                          }}>
                            i
                          </span>
                        </InfoTooltip>
                      )}
                    </div>
                  </div>
                );
              }
            })}
          </div>
        )}
      </div>
    </div>
  );
};

PackageCard.propTypes = {
  method: PropTypes.string,
  onSelectPackage: PropTypes.func,
  packageIndex: PropTypes.number.isRequired,
  selectedIndustry: PropTypes.string.isRequired,
  singlePackage: PropTypes.object.isRequired,
  isMobile: PropTypes.bool.isRequired
};

export default PackageCard;
